import type { Tax, TaxQuery, TaxType } from '@monorepo/shared/schemas/taxes'
import { TAX_TYPE } from '@monorepo/shared/schemas/taxes'
import { Plus, RefreshCw, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useTaxesList } from '../hooks/useTaxes'
import { TaxDrawer } from './TaxDrawer'
import { TaxesTable } from './TaxesTable'

interface TaxesPageProps {
  onViewTax?: (tax: Tax) => void
}

export function TaxesPage({ onViewTax: _onViewTax }: TaxesPageProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all')
  const [typeFilter, setTypeFilter] = useState<TaxType | 'all'>('all')
  const [editingTax, setEditingTax] = useState<Tax | undefined>(undefined)
  const [showTaxFormDrawer, setShowTaxFormDrawer] = useState(false)

  // Create filters object for API
  const filters = useMemo((): TaxQuery => {
    const filterObj: TaxQuery = {}

    if (searchQuery.trim()) {
      filterObj.search = searchQuery.trim()
    }

    if (statusFilter !== 'all') {
      filterObj.is_active = statusFilter === 'active'
    }

    if (typeFilter !== 'all') {
      filterObj.tax_type = typeFilter
    }

    return filterObj
  }, [searchQuery, statusFilter, typeFilter])

  // Fetch taxes data from API using convenience hook
  const { taxes, isLoading, error, refetch } = useTaxesList(filters)

  // Calculate stats from real data
  const activeTaxes = taxes.filter(t => t.is_active).length
  const inactiveTaxes = taxes.filter(t => !t.is_active).length
  const percentageTaxes = taxes.filter(
    t => t.tax_type === TAX_TYPE.percentage
  ).length
  const fixedTaxes = taxes.filter(t => t.tax_type === TAX_TYPE.fixed).length
  const compoundTaxes = taxes.filter(
    t => t.tax_type === TAX_TYPE.compound
  ).length

  const handleCreateTax = () => {
    setEditingTax(undefined)
    setShowTaxFormDrawer(true)
  }

  const handleEditTax = (tax: Tax) => {
    setEditingTax(tax)
    setShowTaxFormDrawer(true)
  }

  const handleTaxClick = (tax: Tax) => {
    // For now, just edit on click
    // In the future, could navigate to detail page
    handleEditTax(tax)
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Taxes'
            subtitle='Manage tax configurations and rates'
          />
          {/* Stats Chips */}
          <div className='flex flex-wrap gap-2 mt-3'>
            <Badge variant='secondary' className='text-sm'>
              {taxes.length} Total
            </Badge>
            <Badge variant='default' className='text-sm'>
              {activeTaxes} Active
            </Badge>
            <Badge variant='outline' className='text-sm'>
              {inactiveTaxes} Inactive
            </Badge>
            <Badge variant='secondary' className='text-sm'>
              {percentageTaxes} Percentage
            </Badge>
            <Badge variant='secondary' className='text-sm'>
              {fixedTaxes} Fixed
            </Badge>
            {compoundTaxes > 0 && (
              <Badge variant='secondary' className='text-sm'>
                {compoundTaxes} Compound
              </Badge>
            )}
          </div>
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            variant='outline'
            size='lg'
            onClick={() => refetch()}
            disabled={isLoading}
            className='flex-initial'
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>
          <Button
            onClick={handleCreateTax}
            size='lg'
            className='flex-1 md:flex-initial'
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Tax
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-2xl'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search taxes by name or code...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select
            value={statusFilter}
            onValueChange={value =>
              setStatusFilter(value as 'all' | 'active' | 'inactive')
            }
          >
            <SelectTrigger className='w-[150px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='active'>Active</SelectItem>
              <SelectItem value='inactive'>Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={typeFilter}
            onValueChange={value => setTypeFilter(value as TaxType | 'all')}
          >
            <SelectTrigger className='w-[150px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Types</SelectItem>
              <SelectItem value={TAX_TYPE.percentage}>Percentage</SelectItem>
              <SelectItem value={TAX_TYPE.fixed}>Fixed</SelectItem>
              <SelectItem value={TAX_TYPE.compound}>Compound</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className='p-4 bg-red-50 text-red-800 rounded-lg'>
          <p className='font-medium'>Error loading taxes</p>
          <p className='text-sm'>{error.message}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className='flex items-center justify-center h-64'>
          <RefreshCw className='w-8 h-8 animate-spin text-muted-foreground' />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && taxes.length === 0 && (
        <div className='flex flex-col items-center justify-center h-64 text-center'>
          <div className='text-muted-foreground mb-4'>
            <p className='text-lg font-medium'>No taxes found</p>
            <p className='text-sm'>
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first tax'}
            </p>
          </div>
          {!searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
            <Button onClick={handleCreateTax}>
              <Plus className='w-4 h-4 mr-2' />
              Create Tax
            </Button>
          )}
        </div>
      )}

      {/* Taxes Table */}
      {!isLoading && !error && taxes.length > 0 && (
        <TaxesTable
          taxes={taxes}
          onEditTax={handleEditTax}
          onViewTax={handleTaxClick}
        />
      )}

      {/* Tax Form Drawer */}
      <TaxDrawer
        tax={editingTax}
        open={showTaxFormDrawer}
        onOpenChange={open => {
          setShowTaxFormDrawer(open)
          if (!open) {
            setEditingTax(undefined)
          }
        }}
      />
    </div>
  )
}
